import { useState, useRef, useEffect } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { FieldContainer } from './FormField';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  required?: boolean;
  error?: string;
}

// Predefined colors from design system
const PREDEFINED_COLORS = [
  { name: 'Green', value: '#10B981' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Gray', value: '#6B7280' },
  { name: 'Slate', value: '#64748B' },
];

export function ColorPicker({
  label,
  value,
  onChange,
  required = false,
  error,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  // Find the selected color name
  const selectedColor = PREDEFINED_COLORS.find((c) => c.value === value);
  const displayName = selectedColor?.name || 'Custom';

  return (
    <FieldContainer
      label={label}
      htmlFor="color-picker"
      required={required}
      error={error}
    >
      <div className="relative" ref={dropdownRef}>
        {/* Dropdown Trigger */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center gap-3 px-3 py-2 text-sm border rounded-md transition-colors bg-background-primary ${
            error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-border-default hover:border-border-focus focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }`}
        >
          <div
            className="w-6 h-6 rounded border border-border-subtle flex-shrink-0"
            style={{ backgroundColor: value }}
          />
          <span className="flex-1 text-left text-text-primary">{displayName}</span>
          <span className="text-xs font-mono text-text-tertiary">{value}</span>
          <ChevronDownIcon
            className={`w-4 h-4 text-text-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-background-elevated border border-border-default rounded-md shadow-lg z-50 p-2">
            {/* Color Grid */}
            <div className="grid grid-cols-6 gap-1.5">
              {PREDEFINED_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => handleColorSelect(color.value)}
                  className={`relative h-8 w-full rounded border transition-all ${
                    value === color.value
                      ? 'border-gray-900 ring-2 ring-gray-900/20'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                >
                  {value === color.value && (
                    <CheckIcon className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </FieldContainer>
  );
}
